function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
/* eslint no-restricted-syntax:"off" -- All LocalStorage modifications must happen through this utility to ensure GDPR compliance. */
/*
  This util exports a set of functions that provide localStorage persistence support.
*/
import { hasConsent, onFirstConsent, OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
import { assert } from '@ember/debug';

// Attempts to store the undefined type in storage will store it as the literal string
// below. However, JSON.parse() errors out on parsing the below string, so we need to
// explicitly check for this case
const UNDEFINED_STRING = 'undefined';
export class Store {
  // We optionally pass in localStorage on instantiation.
  // If localStorage isn't passed in, we use a fake in-memory POJO (ie: in testing)
  constructor(store = {}) {
    _defineProperty(this, "store", void 0);
    _defineProperty(this, "nonPersistingStores", new Map());
    this.store = store;
    for (const cat in OneTrustConsent) {
      const category = OneTrustConsent[cat];
      this.nonPersistingStores.set(category, {});
      this.persistStoreOnConsent(category);
    }
  }

  // We optionally pass category. This is an opt-in solution for OneTrust regressions.
  // If category is passed in, persistable can continue to manage the state for pages
  // which rely on that functionality without writing to localStorage.
  hasKey(key, category) {
    if (!category || hasConsent(category)) {
      return this.store.hasOwnProperty(key); // eslint-disable-line no-prototype-builtins
    } else {
      const store = this.nonPersistingStores.get(category);
      assert('non persisting backup store must exist', store);
      return store.hasOwnProperty(key); // eslint-disable-line no-prototype-builtins
    }
  }

  // We optionally pass category. This is an opt-in solution for OneTrust regressions.
  // If category is passed in, persistable can continue to manage the state for pages
  // which rely on that functionality without writing to localStorage.
  get(key, category) {
    let string;
    if (!category || hasConsent(category)) {
      string = this.store[key];
    } else {
      const store = this.nonPersistingStores.get(category);
      assert('non persisting backup store must exist', store);
      string = store[key];
    }
    if (string === UNDEFINED_STRING) {
      return undefined;
    }
    if (string) {
      try {
        return JSON.parse(string);
      } catch (e) {
        if (e instanceof SyntaxError) {
          // Invalid value found, remove it instead of crashing (and looping since it's persisted)
          this.remove(key);
          return undefined;
        }
        throw e;
      }
    }
    return string;
  }
  set(key, value, category) {
    if (category) {
      const storedValue = value === undefined ? UNDEFINED_STRING : JSON.stringify(value);

      // Trying to JSON.stringify() things like functions will return undefined
      if (storedValue === undefined) {
        throw new Error(`Unable to store value as it could not be JSON stringified. Type was '${typeof value}'`);
      }
      if (hasConsent(category)) {
        this.store[key] = storedValue;
      } else {
        const store = this.nonPersistingStores.get(category);
        assert('non persisting backup store must exist', store);
        store[key] = storedValue;
      }
      return storedValue;
    } else {
      throw new Error(`No OneTrust category provided for '${key}'. All Local Storage entries need to be categorized for EU compliance. Categories are available in 'utils/one-trust/one-trust-compliance'.`);
    }
  }
  clear() {
    if (this.store.clear) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.store.clear();
    } else {
      this.store = {};
    }
    for (const cat in OneTrustConsent) {
      const category = OneTrustConsent[cat];
      this.nonPersistingStores.set(category, {});
    }
  }
  remove(key) {
    delete this.store[key];
    for (const cat in OneTrustConsent) {
      const category = OneTrustConsent[cat];
      delete this.nonPersistingStores.get(category)?.[key];
    }
  }
  persistStoreOnConsent(category) {
    // TODO(DBF-1141): stop passing null here
    onFirstConsent(null, category, () => {
      const store = this.nonPersistingStores.get(category) || {};
      Object.assign(this.store, store);
      this.nonPersistingStores.set(category, {});
      return null;
    });
  }
}
let localStorageEnabled = false;
try {
  // it might exist but not be enabled, such as with private browsing
  // accessing window.localStorage might throw, so we do it in a try/catch. See ONCALL-46649
  if (window.localStorage) {
    window.localStorage.setItem('sq.test', '1');
    window.localStorage.removeItem('sq.test');
    localStorageEnabled = true;
  }
} catch {
  // do nothing... localStorageEnabled is already false
}
const store = localStorageEnabled ? new Store(localStorage) : new Store();
export const persistableMocks = {
  hasKey(key, category) {
    return store.hasKey(key, category);
  },
  get(key, category) {
    return store.get(key, category);
  },
  set(key, val, category) {
    return store.set(key, val, category);
  }
};
export function hasKey(key, category) {
  return persistableMocks.hasKey(key, category);
}
export function get(key, category) {
  return persistableMocks.get(key, category);
}
export function set(key, val, category) {
  return persistableMocks.set(key, val, category);
}
export function getDate(key, category) {
  return new Date(get(key, category));
}
export function setDate(key, value, category) {
  return set(key, value.getTime(), category);
}
export function clear() {
  return store.clear();
}
export function remove(key) {
  return store.remove(key);
}